import { observer } from 'mobx-react'
import { ReactNode, useCallback } from 'react'

import { PetPolicyFieldKind, RevenueModel } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { FlexContainer, Margin, Tooltip } from '@avantstay/backoffice-core'
import { IconBath, IconBed2, IconPets2, IconPinMap, IconUser3 } from '@avantstay/backoffice-vectors'
import { getActiveSortOption, SortColumnIndicator, SortDirection } from '@avantstay/data-grid'

import { ExtendedColumnKey } from '@new/__global/components/FiltersFramework'
import { revenueModelLabel, revenueModelShortLabel } from '@new/__global/constants/revenueModel'
import {
  ExtendedColumnCell,
  ExtendedHeaderCell,
} from '@new/domains/fastBookingsTapeChart/__commonStyles'
import {
  fastBookingsTapeChartStore,
  HomeExtendedInfo,
} from '@new/domains/fastBookingsTapeChart/stores/FastBookingsTapeChartStore'

import * as S from '../ExtendedColumns.styles'
import { arrivalModeColumnKeys } from '../ExtendedColumns.utils'

export const iconsByColumnKey: Record<string, ReactNode> = {
  [ExtendedColumnKey.bedroomsCount]: <IconBed2 />,
  [ExtendedColumnKey.bathroomsCount]: <IconBath />,
  [ExtendedColumnKey.maxOccupancy]: <IconUser3 />,
  [ExtendedColumnKey.distance]: <IconPinMap />,
  [ExtendedColumnKey.revenueModel]: <></>,
  [ExtendedColumnKey.petPolicy]: <IconPets2 />,
}

export const tooltipByColumnKey: Record<string, string> = {
  [ExtendedColumnKey.bedroomsCount]: 'Bedrooms',
  [ExtendedColumnKey.bathroomsCount]: 'Bathrooms',
  [ExtendedColumnKey.maxOccupancy]: 'Guests',
  [ExtendedColumnKey.distance]: 'Proximity to Location',
  [ExtendedColumnKey.revenueModel]: 'Revenue Model',
  [ExtendedColumnKey.petPolicy]: 'Pet Policy',
}

export const labelByColumnKey: Record<string, ReactNode> = {
  [ExtendedColumnKey.revenueModel]: <Margin left={10}>Rev. Model</Margin>,
}

interface ColumnsHeaderProps {
  columnKey: string
  columnWidth: number
  index: number
}

export const ColumnsHeader = observer(({ columnKey, columnWidth, index }: ColumnsHeaderProps) => {
  const { sortOrder, sortBy, setSortOrder } = fastBookingsTapeChartStore

  const getSortDirection = (columnKey?: string) =>
    getActiveSortOption({
      columnKey,
      sortOrder: sortOrder(),
      activeSortKey: sortBy(),
    })

  const handleSort = useCallback(
    (columnKey?: string) => (direction: SortDirection) => {
      setSortOrder({
        sortByKey: columnKey,
        sortOrder: direction,
      })
    },
    [setSortOrder],
  )

  return (
    <ExtendedHeaderCell key={`col-header-${columnKey}-${index}`} width={columnWidth}>
      <S.SortIndicatorWrapper>
        <SortColumnIndicator
          alwaysVisible
          onSort={handleSort(columnKey)}
          sorting={getSortDirection(columnKey)}
        >
          <Tooltip content={tooltipByColumnKey[columnKey]} placement="top">
            <S.IconWrapper>
              {iconsByColumnKey[columnKey]} {labelByColumnKey[columnKey]}
            </S.IconWrapper>
          </Tooltip>
        </SortColumnIndicator>
      </S.SortIndicatorWrapper>
    </ExtendedHeaderCell>
  )
})

export const revenueModelExtendedColumn = (revenueModel?: RevenueModel) => {
  if (!revenueModel) return

  return (
    <Tooltip content={revenueModelLabel[revenueModel]} placement="top">
      <FlexContainer full justify="center">
        {revenueModelShortLabel[revenueModel]}
      </FlexContainer>
    </Tooltip>
  )
}

export const petPolicyExtendedColumn = (petPolicy?: PetPolicyFieldKind) => {
  if (!petPolicy) return

  switch (petPolicy) {
    case PetPolicyFieldKind.approved:
      return 'Yes'
    case PetPolicyFieldKind.notApproved:
      return 'No'
    case PetPolicyFieldKind.unknown:
      return '?'
    default:
      return 'Other'
  }
}

const columnWidth = 60

export const generateFilterColumnByKey = () => {
  const columnsToRender = Object.keys(ExtendedColumnKey).filter(
    columnKey => !arrivalModeColumnKeys.includes(columnKey),
  )

  return columnsToRender?.map((columnKey, index) => ({
    header: <ColumnsHeader columnKey={columnKey} columnWidth={columnWidth} index={index} />,
    columnKey: columnKey,
    width: columnWidth,
    renderer: (props: HomeExtendedInfo | undefined) => {
      const icon = iconsByColumnKey[columnKey]

      let formattedValue: HomeExtendedInfo[keyof HomeExtendedInfo] | JSX.Element =
        props?.[columnKey as keyof typeof props]

      if (columnKey === ExtendedColumnKey.revenueModel) {
        formattedValue = revenueModelExtendedColumn(props?.revenueModel)
      } else if (columnKey === ExtendedColumnKey.petPolicy) {
        formattedValue = petPolicyExtendedColumn(props?.petPolicy)
      }

      return (
        <ExtendedColumnCell key={`col-${columnKey}-${index}-${props?.id}`} width={columnWidth}>
          {formattedValue !== undefined ? (
            <>
              {icon} {formattedValue}
            </>
          ) : (
            'N/A'
          )}
        </ExtendedColumnCell>
      )
    },
  }))
}
